
import React, { useState, useEffect, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const FindHelpDashboard = ({ token }) => {
  const [dashboardModel, setDashboardModel] = useState(null);
  const [findHelpTableData, setFindHelpTableData] = useState([]);
  const [showFindHelpTable, setShowFindHelpTable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const chartRef = useRef();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5199';

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        let referralStatusData = null;
        // Azure authentication logic (commented out):
        /*
        if (token) {
          const referralStatusParams = new URLSearchParams({
            externalUserId: 'PatilSmi',
            userType: 'PowerUser',
            cardGuid: 'a9f0c553-1aa4-41f8-b00c-b76ef358c083',
            MemberIDs: 'QR3M00000070041',
            StartDate: '2024-08-05T00:00:00',
            EndDate: '2025-09-04T00:00:00'
          }).toString();
          const referralStatusResponse = await fetch(`${API_BASE_URL}/api/FindHelp/referralstatus?${referralStatusParams}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (!referralStatusResponse.ok) {
            throw new Error('Failed to fetch FindHelp referral status');
          }
          referralStatusData = await referralStatusResponse.json();
          let memberID = '';
          if (referralStatusData.memberID) {
            memberID = referralStatusData.memberID;
          } else if (referralStatusData.referralStatusList && referralStatusData.referralStatusList.length > 0) {
            memberID = referralStatusData.referralStatusList[0].memberID;
          }
          setDashboardModel({ ...referralStatusData, memberID });
        } else {
          setDashboardModel({
            xValues: [],
            lessThan30Days: [],
            between30And45Days: [],
            greaterThan45Days: []
          });
        }
        */
        // Get parameters from query string
        const urlParams = new URLSearchParams(window.location.search);
        // Hardcoded values for reference:
        // const externalUserId = 'PatilSmi';
        // const userType = 'PowerUser';
        // const cardGuid = 'a9f0c553-1aa4-41f8-b00c-b76ef358c083';
        // const memberIds = 'QR3M00000070041';
        // const startDate = '2024-08-05T00:00:00';
        // const endDate = '2025-09-04T00:00:00';

        // Use query string values:
        const externalUserId = urlParams.get('externalUserId');
        const userType = urlParams.get('userType');
        const cardGuid = urlParams.get('cardGuid');
        const memberIds = urlParams.get('memberIds');
        let startDate = urlParams.get('startDate');
        let endDate = urlParams.get('endDate');

        // Convert MM/DD/YYYY to ISO format if needed
        function toISODate(dateStr) {
          if (!dateStr) return dateStr;
          // Check for MM/DD/YYYY format
          const mmddyyyy = /^(\d{2})\/(\d{2})\/(\d{4})$/;
          const match = decodeURIComponent(dateStr).match(mmddyyyy);
          if (match) {
            const [, mm, dd, yyyy] = match;
            return `${yyyy}-${mm}-${dd}T00:00:00`;
          }
          // Already ISO or other format
          return dateStr;
        }
        startDate = toISODate(startDate);
        endDate = toISODate(endDate);

        const referralStatusParams = new URLSearchParams({
          externalUserId,
          userType,
          cardGuid,
          memberIds,
          startDate,
          endDate
        }).toString();
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const referralStatusResponse = await fetch(`${API_BASE_URL}/api/FindHelp/referralstatus?${referralStatusParams}`, {
          method: 'GET',
          headers
        });
        if (!referralStatusResponse.ok) {
          throw new Error('Failed to fetch FindHelp referral status');
        }
        referralStatusData = await referralStatusResponse.json();
        let memberID = '';
        if (referralStatusData.memberID) {
          memberID = referralStatusData.memberID;
        } else if (referralStatusData.referralStatusList && referralStatusData.referralStatusList.length > 0) {
          memberID = referralStatusData.referralStatusList[0].memberID;
        }
        setDashboardModel({ ...referralStatusData, memberID });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadDashboardData();
  }, [token]);

  // Legend for status categories below chart
  const statusLegend = [
    { color: '#4eaa4e', label: 'Closed Loop - Got Help Status', statuses: ['Got Help'] },
    { color: '#eab00a', label: 'In Progress Status', statuses: ['Not Updated', 'Needs Client Action', 'Pending', 'Eligible'] },
    { color: '#eb4c44', label: "Closed Loop - Didn't get Help", statuses: [
      'Referred elsewhere', 'Could not Get Help', 'Could not Contact',
      'Not Eligible', 'No Capacity', 'No Longer Interested'
    ] }
  ];

  // Chart click handler for FindHelp Dashboard
  const handleFindHelpChartClick = async (event, elements) => {
    if (elements.length > 0) {
      const element = elements[0];
      const status = chartData.labels[element.index];
      const range = '';
      const memberID = dashboardModel.memberID;
      // Get parameters from query string
      const urlParams = new URLSearchParams(window.location.search);
      const externalUserId = urlParams.get('externalUserId');
      const userType = urlParams.get('userType');
      const cardGuid = urlParams.get('cardGuid');
      const gridModelParams = new URLSearchParams({
        externalUserId,
        userType,
        cardGuid,
        memberID,
        referralStatus: status,
        range: range
      }).toString();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      try {
        const response = await fetch(`${API_BASE_URL}/api/FindHelp/gridmodel?${gridModelParams}`, {
          method: 'GET',
          headers
        });
        if (!response.ok) throw new Error('Failed to fetch FindHelp grid model');
        const data = await response.json();
        setFindHelpTableData(data);
        setShowFindHelpTable(data.length > 0);
      } catch (err) {
        setFindHelpTableData([]);
        setShowFindHelpTable(false);
      }
    } else {
      setShowFindHelpTable(false);
    }
  };

  // Export table data to Excel
  const handleExportClick = () => {
    if (!findHelpTableData || findHelpTableData.length === 0) {
      alert('No records to download');
      return;
    }
    import('xlsx').then(XLSX => {
      const ws = XLSX.utils.json_to_sheet(findHelpTableData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'ReferralList');
      XLSX.writeFile(wb, 'FindHelpChart.xlsx');
    });
  };

  if (loading) {
    return (
      <div className="col-12">
        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Find Help Dashboard</h5>
            <div className="text-center">Loading FindHelp data...</div>
          </div>
        </div>
      </div>
    );
  }

  const xValues = dashboardModel && dashboardModel.xValues ? dashboardModel.xValues : [];
  const padArray = (arr) => {
    if (!arr) return Array(xValues.length).fill(0);
    return xValues.map((_, idx) => arr[idx] !== undefined ? arr[idx] : 0);
  };
  const rangeColors = [
    '#4eaa4e', // Less than 30 days
    '#eab00a', // 30-45 Days
    '#eb4c44'  // Greater than 45 Days
  ];
  let chartData = {
    labels: xValues,
    datasets: [
      {
        label: 'Less than 30 days',
        backgroundColor: xValues.map(() => rangeColors[0]),
        maxBarThickness: 40,
        data: padArray(dashboardModel && dashboardModel.lessThan30Days)
      },
      {
        label: '30-45 Days',
        backgroundColor: xValues.map(() => rangeColors[1]),
        maxBarThickness: 40,
        data: padArray(dashboardModel && dashboardModel.between30And45Days)
      },
      {
        label: 'Greater than 45 Days',
        backgroundColor: xValues.map(() => rangeColors[2]),
        maxBarThickness: 40,
        data: padArray(dashboardModel && dashboardModel.greaterThan45Days)
      }
    ]
  };

  return (
    <div className="findhelp-dashboard-page">
  
      <div className="card" style={{ width: '850px', margin: '0 auto' }}>
        <div className="card-body">
          <hr style={{margin: '10px 0'}} />
          <div className="mb-3 text-center">
            <span className="me-3">
              <span style={{display: 'inline-block', width: '30px', height: '16px', backgroundColor: '#4eaa4e', marginRight: '5px'}}></span>
              Less than 30 days
            </span>
            <span className="me-3">
              <span style={{display: 'inline-block', width: '30px', height: '16px', backgroundColor: '#eab00a', marginRight: '5px'}}></span>
              30-45 Days
            </span>
            <span className="me-3">
              <span style={{display: 'inline-block', width: '30px', height: '16px', backgroundColor: '#eb4c44', marginRight: '5px'}}></span>
              Greater than 45 Days
            </span>
          </div>
          <div className="mt-3" style={{ height: '300px' }}>
            <Bar
              ref={chartRef}
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  title: { display: false }
                },
                onClick: handleFindHelpChartClick,
                scales: {
                  x: {
                    grid: { display: true, color: '#e0e0e0' },
                    ticks: {
                      font: { size: 9 },
                      maxRotation: 0,
                      autoSkip: false,
                      color: (context) => {
                        const statusColors = [
                          '#4eaa4e',    // Got Help
                          '#eab00a',    // Not Updated
                          '#eab00a',    // Needs Client Action
                          '#eab00a',    // Pending
                          '#eab00a',    // Eligible
                          '#eb4c44',    // Referred elsewhere
                          '#eb4c44',    // Could not Get Help
                          '#eb4c44',    // Could not Contact
                          '#eb4c44',    // Not Eligible
                          '#eb4c44',    // No Capacity
                          '#eb4c44'     // No Longer Interested
                        ];
                        return statusColors[context.index] || 'black';
                      }
                    }
                  },
                  y: {
                    beginAtZero: true,
                    grid: { display: true, color: '#e0e0e0' },
                    ticks: {
                      stepSize: 1,
                      font: { size: 10 },
                      precision: 0,
                      callback: function(value) {
                        return Number.isInteger(value) ? value : null;
                      }
                    }
                  }
                }
              }}
            />
          </div>
          <div className="mb-2 d-flex justify-content-center">
            <div style={{
              border: '1px solid #000',
              borderRadius: '6px',
              padding: '8px 0',
              margin: '5px auto',
           
              background: '#fff',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap' // <-- added for wrapping
            }}>
              {statusLegend.map((item, idx) => (
                // <span key={idx} className="me-3 d-flex align-items-center" style={{minWidth: '200px', justifyContent: 'center'}}>
                  <span key={idx} className="d-flex align-items-center" style={{minWidth: '200px', justifyContent: 'center'}}>
                  <span style={{
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    backgroundColor: item.color,
                    margin: '2px 2px',
                    border: '1px solid #ceced2'
                  }}></span>
                  <span style={{fontSize: '12px'}}>{item.label}</span>
                </span>
              ))}
            </div>
          </div>
          {showFindHelpTable && findHelpTableData.length > 0 && (
            <div className="mt-4" style={{ padding: '10px', maxWidth: '820px', margin: '0 auto' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-primary mb-2"
                  style={{ fontSize: 11, fontWeight: 'bold', backgroundColor: '#428BCA' }}
                  onClick={handleExportClick}
                >
                  Export Report
                </button>
              </div>
              <style>{`
                .findhelp-table {
                  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
                  border-collapse: collapse;
                  width: 100%;
                  color: #333;
                  background-color: #fff;
                }
                .findhelp-table th, .findhelp-table td {
                  border: 1px solid #ceced2;
                  text-align: center;
                  font-size: 13px;
                }
                .findhelp-table thead tr {
                  background-color: #d3d3d5;
                }
                .findhelp-table tbody tr:nth-child(odd) {
                  background-color: #f9f9f9;
                }
                .findhelp-table tbody tr:nth-child(even) {
                  background-color: #eee;
                }
                .findhelp-table tbody tr:hover td {
                  background-color: #b1b1b1;
                  cursor: auto;
                }
              `}</style>
              <div style={{ overflowX: 'auto' }}>
                <table className="findhelp-table">
                  <thead>
                    <tr>
                      <th>Referral ID</th>
                      <th>Member Name</th>
                      <th>Member ID</th>
                      <th>Medicare</th>
                      <th>Medicaid</th>
                      <th>Staff Name</th>
                      <th>Referral Date</th>
                      <th>Referral Status</th>
                      <th>Referral LastUpdated</th>
                      <th>Provider Name</th>
                      <th>Program Name</th>
                      <th>Category</th>
                      <th>Days Since Referral</th>
                    </tr>
                  </thead>
                  <tbody>
                    {findHelpTableData.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.referralID}</td>
                        <td>{item.memberName}</td>
                        <td>{item.memberID}</td>
                        <td>{item.medicare}</td>
                        <td>{item.medicaid}</td>
                        <td>{item.staffName}</td>
                        <td>{item.referralDate}</td>
                        <td>{item.referralStatus}</td>
                        <td>{item.referralLastUpdated}</td>
                        <td>{item.providerName}</td>
                        <td>{item.programName}</td>
                        <td>{item.category}</td>
                        <td>{item.daysSinceReferral}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FindHelpDashboard;
